
import { supabase } from '@/lib/supabase';
import { ClassSchedule } from '@/types/classTypes';
import { handleError, ErrorSeverity } from '@/utils/errorHandler';

/**
 * Enhanced service for managing class schedules with improved error handling and performance
 */
export const ScheduleService = {
  /**
   * Get all schedules for a class
   */
  async getSchedulesForClass(classId: string): Promise<ClassSchedule[]> {
    try {
      const { data, error } = await supabase
        .from('class_schedules')
        .select('*')
        .eq('class_id', classId)
        .order('weekday', { ascending: true });
        
      if (error) throw error;
      
      return data as ClassSchedule[];
    } catch (error) {
      handleError(
        error, 
        'Error fetching schedules for class', 
        ErrorSeverity.WARNING,
        { additionalInfo: { classId } }
      );
      return [];
    }
  },
  
  /**
   * Get all schedules for an organization in a single efficient query
   */
  async getAllSchedulesForOrganization(organizationId: string): Promise<ClassSchedule[]> {
    try {
      const { data, error } = await supabase
        .from('class_schedules')
        .select('*')
        .eq('organization_id', organizationId)
        .order('weekday', { ascending: true });
        
      if (error) throw error;
      
      return data as ClassSchedule[];
    } catch (error) {
      handleError(
        error, 
        'Error fetching all schedules for organization', 
        ErrorSeverity.WARNING,
        { additionalInfo: { organizationId } }
      );
      return [];
    }
  },
  
  /**
   * Create a new schedule with validation
   */
  async createSchedule(scheduleData: Partial<ClassSchedule>): Promise<ClassSchedule> {
    try {
      // Validate required fields
      if (!scheduleData.organization_id) {
        throw new Error('Organization ID is required to create a schedule');
      }
      
      if (!scheduleData.class_id) {
        throw new Error('Class ID is required to create a schedule');
      }
      
      // Create a safe copy of the data with known structure
      const safeData = {
        class_id: scheduleData.class_id,
        organization_id: scheduleData.organization_id,
        weekday: scheduleData.weekday,
        start_time: scheduleData.start_time,
        end_time: scheduleData.end_time,
        name: scheduleData.name
      };
      
      const { data, error } = await supabase
        .from('class_schedules')
        .insert(safeData)
        .select()
        .single();
      
      if (error) throw error;
      
      return data as ClassSchedule;
    } catch (error) {
      const context = {
        additionalInfo: { 
          organizationId: scheduleData.organization_id,
          classId: scheduleData.class_id 
        }
      };
      
      handleError(
        error, 
        'Error creating schedule', 
        ErrorSeverity.ERROR,
        context
      );
      throw error;
    }
  },
  
  /**
   * Update an existing schedule
   */
  async updateSchedule(
    scheduleId: string, 
    scheduleData: Partial<ClassSchedule>
  ): Promise<ClassSchedule> {
    try {
      // Create a safe copy of the data
      const safeData: Partial<ClassSchedule> = {};
      
      // Only include properties that are present
      if (scheduleData.class_id) safeData.class_id = scheduleData.class_id;
      if (scheduleData.organization_id) safeData.organization_id = scheduleData.organization_id;
      if (scheduleData.weekday !== undefined) safeData.weekday = scheduleData.weekday;
      if (scheduleData.start_time) safeData.start_time = scheduleData.start_time;
      if (scheduleData.end_time) safeData.end_time = scheduleData.end_time;
      if (scheduleData.name) safeData.name = scheduleData.name;
      
      const { data, error } = await supabase
        .from('class_schedules')
        .update(safeData)
        .eq('id', scheduleId)
        .select()
        .single();
      
      if (error) throw error;
      
      return data as ClassSchedule;
    } catch (error) {
      handleError(
        error, 
        'Error updating schedule', 
        ErrorSeverity.ERROR,
        { additionalInfo: { scheduleId } }
      );
      throw error;
    }
  },
  
  /**
   * Delete a schedule
   */
  async deleteSchedule(scheduleId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('class_schedules')
        .delete()
        .eq('id', scheduleId);
      
      if (error) throw error;
      
      return true;
    } catch (error) {
      handleError(
        error, 
        'Error deleting schedule', 
        ErrorSeverity.ERROR,
        { additionalInfo: { scheduleId } }
      );
      return false;
    }
  }
};
